/* Quote Processing Screen, Vue Component (BCR-156 Stage 2) */
<template>
  <div v-if="quoteLoaded">

    <!-- read-only customer submission (spec 6.2.1) -->
    <v-card id="quote-details">
      <v-card-title>
        Quote {{ entity.Reference }}
        <v-chip small :color="statusColor(entity.Status)" outlined class="v-chip-light-bg ml-3">{{ entity.Status }}</v-chip>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <div class="readonly-row"><span class="label">Customer</span><span>{{ entity.MemberId_FullName }} ({{ entity.MemberId_Reference }})</span></div>
            <div class="readonly-row"><span class="label">Email</span><span>{{ entity.MemberId_Email }}</span></div>
            <div class="readonly-row"><span class="label">Retailer</span><span>{{ entity.RetailerName }}</span></div>
            <div class="readonly-row"><span class="label">Item description</span><span>{{ entity.ItemDescription }}</span></div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="readonly-row"><span class="label">Dimensions</span><span>{{ cm(entity.Length) }} &times; {{ cm(entity.Width) }} &times; {{ cm(entity.Height) }} cm</span></div>
            <div class="readonly-row"><span class="label">Weight</span><span>{{ entity.Weight }} kg</span></div>
            <div class="readonly-row"><span class="label">Item value</span><span>{{ Display.Money(entity.ItemValue) }}</span></div>
            <div class="readonly-row"><span class="label">Number of boxes</span><span>{{ entity.NumberOfBoxes }}</span></div>
          </v-col>
          <v-col cols="12" v-if="entity.Comments">
            <div class="readonly-row"><span class="label">Comments</span><span>{{ entity.Comments }}</span></div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- once converted to an order, hide the pricing controls and summarise / link the order -->
    <v-card v-if="entity.OrderId && entity.Status !== 'Pending'" id="quote-order" class="mt-6">
      <v-card-title>{{ convertedOrders.length > 1 ? 'Converted to ' + convertedOrders.length + ' Orders' : 'Converted to Order' }}</v-card-title>
      <v-card-text>
        <!-- a quote priced per box converts into one order per box -->
        <template v-if="convertedOrders.length">
          <div v-for="o in convertedOrders" :key="o.Id" class="readonly-row">
            <span class="label">Order</span>
            <span>
              <router-link class="font-weight-medium text-decoration-none" :to="{ name: 'order-view', params: { id: o.Id } }">{{ o.Reference || ('#' + o.Id) }}</router-link>
              <span class="ms-2">{{ o.StatusName }}</span>
              <span class="ms-2" v-if="o.InvoiceAmount != null">{{ Display.Money(o.InvoiceAmount) }}</span>
            </span>
          </div>
        </template>
        <div v-else class="readonly-row"><span class="label">Order</span><span>
          <router-link class="font-weight-medium text-decoration-none" :to="{ name: 'order-view', params: { id: entity.OrderId } }">{{ entity.OrderId_Reference || ('#' + entity.OrderId) }}</router-link>
        </span></div>
        <div class="readonly-row"><span class="label">Pricing method</span><span>{{ entity.PricingMethod }}</span></div>
        <div class="readonly-row"><span class="label">Quoted cost</span><span>{{ Display.Money(entity.QuotedCost) }}</span></div>
        <div class="readonly-row" v-if="convertedOrders.length > 1"><span class="label">Invoiced total</span><span>{{ Display.Money(entity.OrderId_InvoiceAmount) }}</span></div>
        <div class="mt-4 d-flex flex-wrap align-center">
          <v-btn color="primary" :to="{ name: 'order-view', params: { id: entity.OrderId } }">
            <v-icon size="18" class="me-1">{{ icons.mdiOpenInNew }}</v-icon>
            <span>{{ convertedOrders.length > 1 ? 'View first order' : 'View order' }}</span>
          </v-btn>
          <!-- only offered while the quote made a single order whose invoice is unpaid; the pricing controls
               below are re-enabled and converting again updates that order and replaces its invoice -->
          <action-confirm-dialog v-if="entity.CanReopen" @confirmed="Reopen" :show-comment-form="false" openButtonText="Re-open quote" dialogTitle="Re-open Quote">
            <template v-slot:information>
              <p>
                This re-opens the quote so you can re-price it. <strong>While it is re-opened the customer can't accept or
                pay the invoice</strong> (they'll see that the quote is being re-priced, and aren't emailed yet). When you
                click <strong>Convert to Order</strong> again it updates order
                <strong>{{ entity.OrderId_Reference || ('#' + entity.OrderId) }}</strong> and replaces its invoice
                (the old invoice stays on record). The customer is emailed the new quote.
              </p>
            </template>
            <v-btn color="warning" class="ml-2" :loading="loading"><span>Re-open quote</span></v-btn>
          </action-confirm-dialog>
          <v-btn color="secondary" class="ml-2" @click="Back">
            <v-icon size="18" class="me-1">{{ icons.mdiChevronLeft }}</v-icon>
            <span>Back to quotes</span>
          </v-btn>
        </div>
        <!-- why it can't be re-opened (several orders, or the customer has paid) -->
        <v-alert v-if="entity.Status === 'Completed' && !entity.CanReopen && entity.ReopenBlockedReason"
          type="warning" text dense class="mt-4 mb-0">{{ entity.ReopenBlockedReason }}</v-alert>
      </v-card-text>
    </v-card>

    <!-- pricing / processing (spec 6.2.2 - 6.2.4); read-only once the quote is no longer Pending -->
    <v-card id="quote-process" class="mt-6">
      <v-card-title>{{ readOnly ? 'Quote Pricing' : 'Price Quote' }}</v-card-title>

      <v-card-text>
        <v-alert v-if="serverErrorMessage" type="error">{{ serverErrorMessage }}</v-alert>
        <v-alert v-if="entity.Reopened" type="info" text>
          This quote has been re-opened. Order
          <router-link class="font-weight-medium text-decoration-none" :to="{ name: 'order-view', params: { id: entity.OrderId } }">{{ entity.OrderId_Reference || ('#' + entity.OrderId) }}</router-link>
          still has its previous invoice. Re-price the quote and click <strong>Convert to Order</strong>: that order is
          updated, its invoice is replaced (the old one stays on record) and the customer is emailed the new quote.
          It must stay a single order.
          <div class="mt-2">
            <strong>The customer can't accept or pay this order's invoice while the quote is re-opened.</strong>
            Changed your mind? Cancel the re-quote to put the quote back as it was and make the original invoice payable
            again (the customer isn't emailed).
          </div>
          <action-confirm-dialog @confirmed="CancelRequote" :show-comment-form="false" openButtonText="Cancel re-quote" dialogTitle="Cancel Re-quote">
            <template v-slot:information>
              <p>
                Any changes you have made to this quote since re-opening it will be discarded and it goes back to
                <strong>Completed</strong> as it was. Order <strong>{{ entity.OrderId_Reference || ('#' + entity.OrderId) }}</strong>
                and its original invoice are untouched and can be paid again. The customer is not emailed.
              </p>
            </template>
            <v-btn color="warning" small class="mt-3" :loading="loading"><span>Cancel re-quote</span></v-btn>
          </action-confirm-dialog>
        </v-alert>

        <v-form ref="form">
          <v-row>
            <v-col cols="12" md="6">

              <!-- pricing method: product price OR volumetric (no free-text cost) -->
              <h4 class="mb-1">Pricing method</h4>
              <v-radio-group v-model="form_PricingMethod" row hide-details class="mt-0 mb-4" :disabled="readOnly">
                <v-radio label="Product price" value="Product"></v-radio>
                <v-radio label="Volumetric" value="Volumetric"></v-radio>
              </v-radio-group>

              <!-- Volumetric only: price each box on its own and create one order per box. Product
                   pricing always creates one order per box, so it needs no checkbox. -->
              <v-checkbox
                v-if="!productMode"
                v-model="form_MultipleOrders"
                hide-details
                class="mt-0 mb-4"
                label="Multiple Orders - price each box separately and create one order per box"
                :disabled="readOnly"
              ></v-checkbox>

              <!-- one shared box list for both pricing methods, so sizes carry over when the
                   admin switches between them -->
              <h4 class="mb-1">Boxes</h4>
              <div class="text-caption mb-2" v-if="!readOnly">
                Defaulted to the customer's own dimensions ({{ cm(entity.Length) }} &times; {{ cm(entity.Width) }} &times; {{ cm(entity.Height) }} cm).
                <template v-if="productMode">Change the sizes and the matching product is selected for you; pick a product and its sizes are filled in.</template>
                <template v-else>Edit or add box(es) below to price on the actual measured volume.</template>
              </div>

              <div v-for="(box, i) in boxes" :key="i" class="mb-2">
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <v-text-field v-model="box.L" label="L" suffix="cm" type="number" dense outlined hide-details style="max-width: 92px;" :disabled="readOnly" @input="onBoxDimensionInput(box)"></v-text-field>
                  <span>&times;</span>
                  <v-text-field v-model="box.W" label="W" suffix="cm" type="number" dense outlined hide-details style="max-width: 92px;" :disabled="readOnly" @input="onBoxDimensionInput(box)"></v-text-field>
                  <span>&times;</span>
                  <v-text-field v-model="box.H" label="H" suffix="cm" type="number" dense outlined hide-details style="max-width: 92px;" :disabled="readOnly" @input="onBoxDimensionInput(box)"></v-text-field>
                  <v-text-field v-model="box.Qty" label="Qty" type="number" dense outlined hide-details style="max-width: 70px;" :disabled="readOnly"></v-text-field>
                  <ProductSelect
                    v-if="productMode"
                    :value="box.ProductId"
                    @input="onBoxProductInput(box, $event)"
                    outlined dense hide-details
                    :disabled="readOnly"
                    :exclude-codes="['MPC']"
                    style="min-width: 230px;"
                  />
                  <v-btn v-if="!readOnly" icon small @click="removeBox(i)"><v-icon>{{ icons.mdiClose }}</v-icon></v-btn>
                </div>
                <div v-if="productMode" class="text-caption ms-1 mt-1">
                  <span v-if="box.ProductId">
                    Product price: <strong>{{ Display.Money(boxProductPrice(box)) }}</strong>
                    <span v-if="boxQty(box) > 1"> &times; {{ boxQty(box) }} = <strong>{{ Display.Money(boxProductPrice(box) * boxQty(box)) }}</strong></span>
                  </span>
                  <span v-else class="error--text">
                    No product fits these dimensions &mdash; choose one, or price this quote volumetrically.
                  </span>
                </div>
              </div>

              <div class="d-flex align-center mb-4" style="gap: 8px;" v-if="!readOnly">
                <v-btn small text @click="addBox"><v-icon left size="18">{{ icons.mdiPlus }}</v-icon>Add box</v-btn>
                <v-btn small color="primary" @click="Save" :loading="loading">
                  <v-icon left size="18">{{ icons.mdiRefresh }}</v-icon>Save &amp; recalculate
                </v-btn>
              </div>

              <v-alert v-if="orderCount > 1" type="info" text dense class="mb-4">
                Converting this quote will create <strong>{{ orderCount }} orders</strong> &mdash; one per box.
                Each is invoiced and paid separately, and each carries the additional costs below in full
                &mdash; except Heavy item, which is charged once against the first package.
              </v-alert>

              <h4 class="mb-1 mt-2">Quoted cost</h4>
              <output class="text-h5 info--text">{{ Display.Money(effectiveCost) }}</output>
              <div class="text-caption mb-4">
                Derived from the {{ form_PricingMethod === 'Product' ? 'selected product price' : 'volumetric price' }}.
              </div>

              <h4 class="mb-2 mt-2">Additional Costs</h4>
              <div class="text-caption mb-1" v-if="orderCount > 1">
                Applied to every package this quote creates.
              </div>
              <div class="d-flex flex-wrap" style="gap: 0 1.5rem">
                <!-- Heavy is the one extra that is not applied to every package - see heavyNote -->
                <v-checkbox v-if="showExtra('Heavy', form_HeavyItem)" v-model="form_HeavyItem" hide-details  :disabled="readOnly || heavyDisabled">
<template #label>Heavy item <v-chip v-if="isAdminOnlyExtra('Heavy')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                <v-checkbox v-if="showExtra('Additional Compensation', form_CompensationRequired)" v-model="form_CompensationRequired" hide-details  :disabled="readOnly">
<template #label>Compensation <v-chip v-if="isAdminOnlyExtra('Additional Compensation')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                <v-checkbox v-if="showExtra('Dangerous Goods Handling', form_DangerousGoods)" v-model="form_DangerousGoods" hide-details  :disabled="readOnly">
<template #label>Dangerous goods <v-chip v-if="isAdminOnlyExtra('Dangerous Goods Handling')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                <v-checkbox v-if="showExtra('Packaging', form_PackagingRequired)" v-model="form_PackagingRequired" hide-details  :disabled="readOnly">
<template #label>Packaging <v-chip v-if="isAdminOnlyExtra('Packaging')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                <v-checkbox v-if="showExtra('HIAB Charge', form_HIABCharge)" v-model="form_HIABCharge" hide-details  :disabled="readOnly">
<template #label>HIAB Charge <v-chip v-if="isAdminOnlyExtra('HIAB Charge')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                <!-- every other enabled extra, customer-selectable and admin-only alike -->
                <v-checkbox v-for="extra in otherExtraOptions" :key="extra.Id" v-model="form_ExtraIds" :value="extra.Id"
                  hide-details :disabled="readOnly" :color="extra.AdminOnly ? 'warning' : undefined">
                  <template #label>
                    {{ extra.Name }}
                    <v-chip v-if="extra.AdminOnly" x-small color="warning" class="ml-2">Admin only</v-chip>
                  </template>
                </v-checkbox>
              </div>
              <div class="text-caption mt-1" v-if="heavyNote">{{ heavyNote }}</div>

              <h4 class="mb-2 mt-6">Remarks{{ remarksRequired ? ' *' : '' }}</h4>
              <div class="text-caption mb-2" v-if="remarksRequired && !readOnly">
                Required &mdash; this quote becomes {{ orderCount }} separate orders, and your remarks are
                emailed to the customer and shown on their quote to explain the split.
              </div>
              <v-textarea
                label="Remarks (also used as the rejection reason)"
                dense outlined flat
                v-model="form_Remarks"
                :readonly="readOnly"
                :rules="remarksRequired ? [validators.required] : []"
              ></v-textarea>

            </v-col>

            <v-col cols="12" md="6">

              <!-- single pricing summary: shows product OR volumetric detail, then surcharges + total (spec 6.2.3) -->
              <v-card outlined class="pricing-summary">
                <v-card-title class="text-h6">Pricing Summary</v-card-title>
                <v-card-text>

                  <!-- method-specific detail -->
                  <template v-if="productMode">
                    <div v-for="(box, i) in pricedBoxes" :key="i" class="summary-row">
                      <span>
                        {{ box.L }}&times;{{ box.W }}&times;{{ box.H }} cm<span v-if="boxQty(box) > 1"> &times; {{ boxQty(box) }}</span>
                        &mdash; {{ boxProductLabel(box) }}
                      </span>
                      <span>{{ Display.Money(boxProductPrice(box) * boxQty(box)) }}</span>
                    </div>
                    <div v-if="!pricedBoxes.length" class="summary-row"><span>Product</span><span>&mdash;</span></div>
                  </template>
                  <template v-else-if="entity.Volumetric && entity.Volumetric.HasDimensions">

                    <!-- each box priced on its own (one order per box) -->
                    <template v-if="entity.Volumetric.MultipleOrders && volumetricLines.length">
                      <div v-for="(line, i) in volumetricLines" :key="i" class="mb-3">
                        <div class="summary-row font-weight-medium">
                          <span>
                            {{ cm(line.L) }}&times;{{ cm(line.W) }}&times;{{ cm(line.H) }} cm<span v-if="line.Qty > 1"> &times; {{ line.Qty }}</span>
                          </span>
                          <span>{{ Display.Money(line.LineTotal) }}</span>
                        </div>
                        <div class="summary-row text-caption"><span>Volume (per box)</span><span>{{ line.Volume }} cm&sup3;</span></div>
                        <div class="summary-row text-caption"><span>Volume price ({{ entity.Volumetric.PricePerVolume }} / cm&sup3;)</span><span>{{ Display.Money(line.VolumePrice) }}</span></div>
                        <div class="summary-row text-caption"><span>Fuel surcharge ({{ entity.Volumetric.FuelSurchargePercent }}%)</span><span>{{ Display.Money(line.FuelSurchargeAmount) }}</span></div>
                        <!-- per line: the heavy package is priced with no flat base, since the heavy
                             charge replaces it. Only ever the first package of the quote. -->
                        <div class="summary-row text-caption"><span>Base</span><span>{{ Display.Money(line.Base) }}</span></div>
                        <div class="summary-row text-caption"><span>Margin ({{ entity.Volumetric.MarginPercent }}%)</span><span>{{ Display.Money(line.MarginAmount) }}</span></div>
                        <div class="summary-row text-caption font-weight-medium"><span>Price per box</span><span>{{ Display.Money(line.UnitTotal) }}</span></div>
                        <!-- only differs on the row carrying the heavy package: its first box has no
                             base, the rest of the row does -->
                        <div v-if="line.Qty > 1 && line.RepeatUnitTotal !== line.UnitTotal" class="summary-row text-caption">
                          <span>Remaining {{ line.Qty - 1 }} in this row</span>
                          <span>{{ Display.Money(line.RepeatUnitTotal) }} each</span>
                        </div>
                      </div>
                    </template>

                    <!-- all boxes priced together as one volume (a single order) -->
                    <template v-else>
                      <div class="summary-row"><span>Volume</span><span>{{ entity.Volumetric.Volume }} cm&sup3;</span></div>
                      <div class="summary-row"><span>Volume price ({{ entity.Volumetric.PricePerVolume }} / cm&sup3;)</span><span>{{ Display.Money(entity.Volumetric.VolumePrice) }}</span></div>
                      <div class="summary-row"><span>Fuel surcharge ({{ entity.Volumetric.FuelSurchargePercent }}%)</span><span>{{ Display.Money(entity.Volumetric.FuelSurchargeAmount) }}</span></div>
                      <div class="summary-row"><span>Base</span><span>{{ Display.Money(entity.Volumetric.Base) }}</span></div>
                      <div class="summary-row"><span>Margin ({{ entity.Volumetric.MarginPercent }}%)</span><span>{{ Display.Money(entity.Volumetric.MarginAmount) }}</span></div>
                    </template>
                  </template>

                  <v-divider class="my-2"></v-divider>

                  <div class="summary-row font-weight-medium"><span>Quoted cost ({{ productMode ? 'product' : 'volumetric' }})</span><span>{{ Display.Money(effectiveCost) }}</span></div>

                  <!-- every order created from this quote carries the additional costs in full, so a
                       quote split into N orders is charged each of them N times. Heavy is the
                       exception: it is the two-person charge for one package, so it lands on the
                       first order only and is charged once however many orders there are. -->
                  <template v-if="form_HeavyItem">
                    <div class="summary-row"><span>Heavy handling<span v-if="orderCount > 1"> (first package only)</span></span><span>{{ Display.Money(extraTotal('Heavy')) }}</span></div>
                  </template>
                  <template v-if="form_DangerousGoods">
                    <div class="summary-row"><span>Dangerous goods<span v-if="orderCount > 1"> &times; {{ orderCount }}</span></span><span>{{ Display.Money(extraTotal('Dangerous Goods Handling')) }}</span></div>
                  </template>
                  <template v-if="form_PackagingRequired">
                    <div class="summary-row"><span>Packaging<span v-if="orderCount > 1"> &times; {{ orderCount }}</span></span><span>{{ Display.Money(extraTotal('Packaging')) }}</span></div>
                  </template>
                  <template v-if="form_CompensationRequired">
                    <div class="summary-row"><span>Additional compensation<span v-if="orderCount > 1"> &times; {{ orderCount }}</span></span><span>{{ Display.Money(extraTotal('Additional Compensation')) }}</span></div>
                  </template>
                  <template v-if="form_HIABCharge">
                    <div class="summary-row"><span>HIAB Charge<span v-if="orderCount > 1"> &times; {{ orderCount }}</span></span><span>{{ Display.Money(extraTotal('HIAB Charge')) }}</span></div>
                  </template>
                  <div v-for="extra in selectedOtherExtras" :key="'extra-' + extra.Id" class="summary-row">
                    <span>{{ extra.Name }}<span v-if="orderCount > 1"> &times; {{ orderCount }}</span></span>
                    <span>{{ Display.Money(extraTotal(extra.Name)) }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="summary-row text-h6">
                    <span>Total estimated<span v-if="orderCount > 1" class="text-caption"> ({{ orderCount }} orders)</span></span>
                    <span class="info--text">{{ Display.Money(totalEstimated) }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- action buttons (spec 6.2.4) -->
      <v-card-text v-if="entity.Status == 'Pending'">
        <v-row class="align-center">
          <v-col class="d-flex flex-wrap align-center">
            <v-btn color="primary" class="me-3" @click="Save" :loading="loading">
              <v-icon size="18" class="me-1">{{ icons.mdiContentSaveOutline }}</v-icon>
              <span>Save</span>
            </v-btn>
            <action-confirm-dialog v-if="canConvert" @confirmed="Convert" :show-comment-form="false" openButtonText="Convert to Order" dialogTitle="Convert to Order">
              <template v-slot:information>
                <p v-if="orderCount > 1">
                  This creates <strong>{{ orderCount }} separate orders</strong> from this quote &mdash; one per box &mdash;
                  and emails the customer a link to accept &amp; pay for each. Save any pricing changes first.
                </p>
                <p v-else-if="entity.Reopened">
                  This updates order <strong>{{ entity.OrderId_Reference || ('#' + entity.OrderId) }}</strong> with the new pricing and
                  replaces its invoice (the old invoice stays on record), then emails the customer the new quote with a link to accept &amp; pay.
                </p>
                <p v-else>This creates an order from the quote and emails the customer a link to accept &amp; pay. Save any pricing changes first.</p>
              </template>
              <v-btn color="success" class="me-3"><span>Convert to Order</span></v-btn>
            </action-confirm-dialog>
            <v-btn v-else color="success" class="me-3" disabled :title="convertDisabledReason"><span>Convert to Order</span></v-btn>
          </v-col>
          <v-col class="d-flex flex-wrap align-center justify-end">
            <action-confirm-dialog v-if="canReject && !entity.Reopened" @confirmed="Reject" :show-comment-form="false" openButtonText="Reject Quote" dialogTitle="Reject Quote">
              <template v-slot:information>
                <p>This quote will be rejected and the customer emailed with your remarks above as the reason.</p>
              </template>
              <v-btn color="error" class="me-3">
                <span>Reject Quote</span>
              </v-btn>
            </action-confirm-dialog>
            <v-btn v-else color="error" class="me-3" disabled title="Enter Remarks to enable rejection">Reject Quote</v-btn>

            <v-btn color="secondary" @click="Back">
              <v-icon size="18" class="me-1">{{ icons.mdiChevronLeft }}</v-icon>
              <span>Back</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-else-if="!entity.OrderId">
        <v-btn color="secondary" @click="Back">
          <v-icon size="18" class="me-1">{{ icons.mdiChevronLeft }}</v-icon>
          <span>Back</span>
        </v-btn>
      </v-card-text>

    </v-card>
  </div>
</template>

<script>
import store from '@/store'
import { useRouter } from '@core/utils'
import * as Display from '@/datahelpers/displayTypes'
import * as FrameworkValidators from '@core/utils/validation'
import * as Validators from '@/datahelpers/validation'
import ProductSelect from '@/views/product/ProductSelect'
import ActionConfirmDialog from '@/views/helpers/ActionConfirmDialog'
import { mdiContentSaveOutline, mdiChevronLeft, mdiClose, mdiPlus, mdiRefresh, mdiOpenInNew } from '@mdi/js'
import { ref, computed, watch } from '@vue/composition-api'

export default {
  components: {
    ProductSelect,
    ActionConfirmDialog
  },
  setup() {

    const { route, router } = useRouter();

    const quoteLoaded = ref(false);
    const loading = ref(false);
    const form = ref(null);
    const serverErrorMessage = ref(null);

    // editable fields
    const form_PricingMethod = ref('Volumetric');
    const form_CompensationRequired = ref(false);
    const form_HeavyItem = ref(false);
    const form_DangerousGoods = ref(false);
    const form_PackagingRequired = ref(false);
    const form_HIABCharge = ref(false);
    const form_Remarks = ref(null);
    const form_MultipleOrders = ref(false);
    // One shared box list for BOTH pricing methods, in cm: { L, W, H, Qty, ProductId }.
    // ProductId is only meaningful for product pricing, but it is kept when switching to volumetric
    // so the admin's work survives a round trip between the two.
    const boxes = ref([]);

    const entity = computed(() => store.state.app.Quote || {});
    // only a Pending quote is editable; once converted/rejected/cancelled it's read-only
    const readOnly = computed(() => entity.value && entity.value.Status !== 'Pending');

    const validators = { ...FrameworkValidators, ...Validators };

    const cm = (mm) => (mm === null || mm === undefined || mm === '') ? '' : Math.round(mm / 10);

    const statusColor = (status) => {
      if (status == 'Pending') return 'warning';
      if (status == 'Completed') return 'success';
      if (status == 'Rejected') return 'error';
      if (status == 'Cancelled') return 'secondary';
      return 'primary';
    };

    // ---- product pricing ----
    const products = computed(() => store.state.app.Products || []);
    const productMode = computed(() => form_PricingMethod.value === 'Product');
    const productById = (id) => products.value.find(x => x.Id == id) || null;

    const boxQty = (box) => Math.max(1, parseInt(box && box.Qty) || 1);
    const boxProductPrice = (box) => {
      const p = box ? productById(box.ProductId) : null;
      return p ? p.Price : 0;
    };
    const boxProductLabel = (box) => {
      const p = box ? productById(box.ProductId) : null;
      return p ? `${p.Code} - ${p.Name}` : '—';
    };

    // rows with usable dimensions - what actually becomes orders
    const validBoxes = computed(() => boxes.value.filter(b => b.L && b.W && b.H));
    // ...of those, the ones that have a product (product pricing)
    const pricedBoxes = computed(() => validBoxes.value.filter(b => b.ProductId));
    const productBoxesTotal = computed(() =>
      pricedBoxes.value.reduce((sum, b) => sum + (boxProductPrice(b) * boxQty(b)), 0));

    // one order per box for product pricing always, and for volumetric when Multiple Orders is set
    const perBoxOrders = computed(() => productMode.value || form_MultipleOrders.value);
    const orderCount = computed(() => {
      if (!perBoxOrders.value || !validBoxes.value.length) return 1;
      return validBoxes.value.reduce((n, b) => n + boxQty(b), 0);
    });
    const remarksRequired = computed(() => orderCount.value > 1);

    // Heavy (two-person handling) is not available on a PRODUCT-priced quote that becomes more than
    // one package - a standard product's price already assumes a one-person package. Volumetric
    // pricing may carry it, charged against the first package only. Mirrors QuoteService.HeavyAllowed.
    const heavyDisabled = computed(() => productMode.value && orderCount.value > 1);
    const heavyNote = computed(() => {
      if (heavyDisabled.value) {
        return `Heavy item is not available on a product-priced quote that creates ${orderCount.value} packages.`;
      }
      if (form_HeavyItem.value && orderCount.value > 1) {
        return 'Heavy item is charged once, against the first package - it also replaces that package\'s flat base, so Save & recalculate to see the price change.';
      }
      return null;
    });

    const volumetricLines = computed(() =>
      (entity.value.Volumetric && entity.value.Volumetric.Lines) || []);

    const convertedOrders = computed(() => entity.value.Orders || []);

    // ---- effective (derived) quoted cost ----
    const effectiveCost = computed(() => {
      if (productMode.value) return productBoxesTotal.value || 0;
      // volumetric - reflects the last saved recalculation from the server
      return (entity.value.Volumetric && entity.value.Volumetric.Total) || 0;
    });

    // ---- surcharges (order extras) ----
    const orderExtras = ref([]);
    const surchargePrice = (name) => {
      const e = orderExtras.value.find(x => x.Name == name);
      return e ? e.Price : 0;
    };
    // Each order created from this quote carries the extras IN FULL, so an extra costs its unit
    // price once per resulting order. Heavy is the exception - the two-person charge is for one
    // physical package, so it is charged once against the first order however many there are.
    const extraTotal = (name) => {
      const times = name === 'Heavy' ? 1 : orderCount.value;
      return Math.round(surchargePrice(name) * times * 100) / 100;
    };
    const totalEstimated = computed(() => {
      let total = Number(effectiveCost.value) || 0;
      if (form_HeavyItem.value) total += extraTotal('Heavy');
      if (form_DangerousGoods.value) total += extraTotal('Dangerous Goods Handling');
      if (form_PackagingRequired.value) total += extraTotal('Packaging');
      if (form_CompensationRequired.value) total += extraTotal('Additional Compensation');
      if (form_HIABCharge.value) total += extraTotal('HIAB Charge');
      selectedOtherExtras.value.forEach(e => { total += extraTotal(e.Name); });
      return Math.round(total * 100) / 100;
    });

    // a flag checkbox is shown only while its extra is enabled, so disabling an extra removes it from this
    // screen. One already ticked on this quote stays visible (so it can be unticked), and nothing is
    // hidden until the extras have loaded.
    const showExtra = (name, alreadyOn) => {
      if (orderExtras.value.length === 0 || alreadyOn) return true;
      return orderExtras.value.some(e => e.Name === name && e.Enabled);
    };

    // true when the named extra is flagged Admin Only (drives the badge)
    const isAdminOnlyExtra = (name) => {
      const extra = orderExtras.value.find(e => e.Name === name);
      return !!(extra && extra.AdminOnly);
    };

    // any enabled extra beyond the five with their own flag; ticked ones are saved as ExtraIds
    const FLAG_EXTRAS = ['Heavy', 'Dangerous Goods Handling', 'Packaging', 'Additional Compensation', 'HIAB Charge'];
    const form_ExtraIds = ref([]);
    const otherExtraOptions = computed(() => orderExtras.value.filter(e => e.Enabled && !e.IsCollectionFee && !FLAG_EXTRAS.includes(e.Name)));
    const selectedOtherExtras = computed(() => otherExtraOptions.value.filter(e => form_ExtraIds.value.includes(e.Id)));

    const fetchOrderExtras = () => {
      store.dispatch('app/fetchOrderExtras', {
        search: {},
        options: { sortBy: ['SortOrder'], sortDesc: [false], itemsPerPage: 200 },
        filter: {}
      }).then(response => {
        if (response && response.data && response.data.items) {
          orderExtras.value = response.data.items;
        }
      }).catch(error => console.log(error));
    };

    // ---- boxes ----
    const addBox = () => boxes.value.push({ L: null, W: null, H: null, Qty: 1, ProductId: null });
    const removeBox = (i) => boxes.value.splice(i, 1);

    // parse stored boxes (mm) into editable cm rows
    const parseBoxes = (json) => {
      if (!json) return [];
      try {
        const arr = JSON.parse(json);
        if (!Array.isArray(arr)) return [];
        return arr.map(b => ({
          L: b.L / 10, W: b.W / 10, H: b.H / 10,
          Qty: b.Qty || 1,
          // absent on boxes saved before per-box products existed
          ProductId: b.ProductId || null
        }));
      } catch (e) {
        return [];
      }
    };

    // valid cm rows -> mm JSON for the server
    const boxesJson = () => {
      const valid = validBoxes.value
        .map(b => ({
          L: Math.round(Number(b.L) * 10),
          W: Math.round(Number(b.W) * 10),
          H: Math.round(Number(b.H) * 10),
          Qty: boxQty(b),
          // sent in both modes so a switch to volumetric and back doesn't lose the products
          ProductId: b.ProductId || null
        }));
      return JSON.stringify(valid);
    };

    // ---- auto-fit: cheapest non-MPC product whose box (mm) fits the given dimensions (mm) ----
    const bestFitProduct = (l, w, h) => {
      if (!l || !w || !h) return null;
      const fits = (products.value || []).filter(p =>
        p.Enabled && p.Public && p.Code !== 'MPC' &&
        p.Length >= l && p.Width >= w && p.Height >= h
      );
      if (!fits.length) return null;
      return fits.reduce((best, p) => (p.Price < best.Price ? p : best), fits[0]);
    };

    // does the quote's own (originally submitted) dimensions fit an existing product?
    const autoFitProduct = () => {
      const q = entity.value;
      return bestFitProduct(q.Length, q.Width, q.Height);
    };

    // default volumetric box: one box matching the customer's own submitted dimensions
    const defaultBoxFromEntity = () => {
      const q = entity.value;
      if (!q.Length || !q.Width || !q.Height) return [];
      return [{ L: cm(q.Length), W: cm(q.Width), H: cm(q.Height), Qty: q.NumberOfBoxes || 1, ProductId: null }];
    };

    // re-check product fit as the admin adjusts a box's dimensions (cm)
    const fitProductForBox = (box) => {
      if (!box || !box.L || !box.W || !box.H) return null;
      return bestFitProduct(
        Math.round(Number(box.L) * 10),
        Math.round(Number(box.W) * 10),
        Math.round(Number(box.H) * 10)
      );
    };

    // Two-way sync between a box's dimensions and its product.
    //
    // Loop prevention: these are @input / @change HANDLERS, not watchers - assigning box.L or
    // box.ProductId in code does not re-fire them, so an automatic update cannot cascade back into
    // the field that triggered it. (Do NOT convert these to watch() - that is exactly what would
    // make them ping-pong.) `syncing` is a second line of defence in case a Vuetify field echoes
    // an input event back while we are writing to it.
    let syncing = false;

    // dimensions changed -> pick the product that fits, or none if nothing does
    const onBoxDimensionInput = (box) => {
      if (!productMode.value || readOnly.value || syncing) return;
      syncing = true;
      const fit = fitProductForBox(box);
      box.ProductId = fit ? fit.Id : null;
      syncing = false;
    };

    // product chosen -> set the box to that product's dimensions
    const onBoxProductInput = (box, productId) => {
      if (readOnly.value || syncing) return;
      syncing = true;
      box.ProductId = productId || null;
      const p = productById(productId);
      if (p) {
        box.L = cm(p.Length);
        box.W = cm(p.Width);
        box.H = cm(p.Height);
      }
      syncing = false;
    };

    // seed each box's product from its dimensions (used when arriving from volumetric pricing -
    // boxes that fit nothing are simply left without a product)
    const autoFitBoxProducts = () => {
      boxes.value.forEach(box => {
        if (box.ProductId) return;
        const fit = fitProductForBox(box);
        if (fit) box.ProductId = fit.Id;
      });
    };

    // ---- load quote ----
    const initFields = () => {
      const q = entity.value;
      const savedBoxes = parseBoxes(q.Boxes);
      // an explicit SelectedProductId or a saved box list means staff have actually priced this before -
      // PricingMethod alone isn't reliable for that, since Convert() force-saves it even when the pricing
      // form was only ever left at its auto-computed default
      const hasExplicitPricing = !!q.SelectedProductId || savedBoxes.length > 0;

      form_MultipleOrders.value = !!q.MultipleOrders;
      boxes.value = savedBoxes.length ? savedBoxes : defaultBoxFromEntity();

      if (q.Status !== 'Pending' || hasExplicitPricing) {
        // finalised, or already explicitly priced - respect the saved values, no auto-fit override
        form_PricingMethod.value = q.PricingMethod || 'Volumetric';
        // a quote priced before per-box products existed carries its product on the quote rather
        // than on a box - put it on the first box so it shows up in the per-box editor
        if (q.SelectedProductId && boxes.value.length && !boxes.value.some(b => b.ProductId)) {
          boxes.value[0].ProductId = q.SelectedProductId;
        }
      } else {
        // still pending and never explicitly priced - always re-check fit on load
        const fit = autoFitProduct();
        if (fit) {
          form_PricingMethod.value = 'Product';
          if (boxes.value.length) boxes.value[0].ProductId = fit.Id;
        } else {
          form_PricingMethod.value = 'Volumetric';
        }
      }
      form_CompensationRequired.value = !!q.CompensationRequired;
      form_HeavyItem.value = !!q.HeavyItem;
      form_DangerousGoods.value = !!q.DangerousGoods;
      form_PackagingRequired.value = !!q.PackagingRequired;
      form_HIABCharge.value = !!q.HIABCharge;
      form_ExtraIds.value = q.ExtrasIds ? q.ExtrasIds.slice() : [];
      // a saved quote can carry Heavy from before it grew a second package (or from the customer's
      // own submission) - the rule applies on load too, not only when the admin changes something
      if (heavyDisabled.value) form_HeavyItem.value = false;
      form_Remarks.value = q.Remarks || null;
    };

    const fetchData = (id) => {
      Promise.all([
        store.dispatch('app/fetchQuote', { id }),
        store.dispatch('app/fetchProducts', {})
      ])
        .then(() => {
          initFields();
          quoteLoaded.value = true;
        })
        .catch(error => {
          console.log(error);
          router.push({ name: 'order-quoteslist' });
        });
    };

    // Switching to product pricing carries the volumetric sizes over and picks a product for each
    // box; boxes that fit nothing are left unset for the admin to resolve. Watching the MODE is safe -
    // it is not one of the two fields that sync to each other, so it cannot cause a loop.
    watch(form_PricingMethod, (method) => {
      if (method === 'Product' && !readOnly.value) {
        autoFitBoxProducts();
        // switching to product pricing drops Heavy - a standard product's price already assumes a
        // one-person package. Re-tickable by hand while the quote is still a single package.
        form_HeavyItem.value = false;
      }
    });

    // ...and adding boxes while already in product mode has to clear it too, otherwise a quote that
    // was heavy as one package silently keeps the surcharge once it becomes several
    watch(heavyDisabled, (disabled) => {
      if (disabled && !readOnly.value) form_HeavyItem.value = false;
    });

    watch(() => route.value.params.id, (id) => fetchData(id));
    fetchData(route.value.params.id);
    fetchOrderExtras();

    // ---- rules for buttons (spec 6.2.4) ----
    const canReject = computed(() => !!(form_Remarks.value && form_Remarks.value.trim().length));
    // Null when the quote can be converted, otherwise why not (mirrors the server's
    // QuoteService.ValidateForConversion, so the button explains what the API would refuse).
    const convertDisabledReason = computed(() => {
      if (productMode.value) {
        if (!validBoxes.value.length) return "Enter at least one box size before converting.";
        if (validBoxes.value.some(b => !b.ProductId)) {
          return "Every box needs a product selected before converting.";
        }
      } else if (!(entity.value.Volumetric && entity.value.Volumetric.HasDimensions)) {
        return "Set a volumetric price before converting.";
      }
      if (remarksRequired.value && !(form_Remarks.value && form_Remarks.value.trim().length)) {
        return `Remarks are required - this quote becomes ${orderCount.value} separate orders.`;
      }
      // a re-opened quote updates its one existing order in place, so it can't be split into several
      if (entity.value.Reopened && orderCount.value > 1) {
        return "A re-opened quote must stay a single order - remove the per-box / multiple-order pricing.";
      }
      return null;
    });
    const canConvert = computed(() => convertDisabledReason.value === null);

    const buildPayload = () => ({
      PricingMethod: form_PricingMethod.value,
      // the first box's product, kept for backwards compatibility (per-box products live in Boxes)
      SelectedProductId: productMode.value ? ((pricedBoxes.value[0] && pricedBoxes.value[0].ProductId) || null) : null,
      Boxes: boxesJson(),
      MultipleOrders: form_MultipleOrders.value,
      CompensationRequired: form_CompensationRequired.value,
      HeavyItem: form_HeavyItem.value,
      DangerousGoods: form_DangerousGoods.value,
      PackagingRequired: form_PackagingRequired.value,
      HIABCharge: form_HIABCharge.value,
      // only ids the list actually offers - the list may not have loaded, in which case leave them alone
      ExtraIds: orderExtras.value.length > 0 ? selectedOtherExtras.value.map(e => e.Id) : null,
      Remarks: form_Remarks.value
    });

    const Save = () => {
      serverErrorMessage.value = null;
      loading.value = true;
      store.dispatch('app/updateQuote', { id: route.value.params.id, quote: buildPayload() })
        .then(() => {
          // re-sync editable fields from the server (volumetric recalculated, cost derived)
          initFields();
          loading.value = false;
        })
        .catch(error => {
          loading.value = false;
          if (error && error.message) serverErrorMessage.value = error.message;
          else console.log(error);
        });
    };

    const Reject = () => {
      loading.value = true;
      store.dispatch('app/rejectQuote', { id: route.value.params.id, remarks: form_Remarks.value })
        .then(() => {
          loading.value = false;
          router.push({ name: 'order-quoteslist' });
        })
        .catch(error => {
          loading.value = false;
          console.log(error);
        });
    };

    const Convert = () => {
      serverErrorMessage.value = null;
      loading.value = true;
      // always persist the current pricing form first - otherwise a quote that was never explicitly
      // Saved (e.g. the auto-selected default was accepted as-is) converts using whatever QuotedCost/
      // Boxes/PricingMethod are already in the DB, which can still be null, producing a £0 order with
      // no size to show the customer.
      store.dispatch('app/updateQuote', { id: route.value.params.id, quote: buildPayload() })
        .then(() => store.dispatch('app/convertQuote', { id: route.value.params.id }))
        .then(response => {
          loading.value = false;
          const q = response.data;
          if (q && q.OrderId) router.push({ name: 'order-view', params: { id: q.OrderId } });
          else router.push({ name: 'order-quoteslist' });
        })
        .catch(error => {
          loading.value = false;
          if (error && error.message) serverErrorMessage.value = error.message;
          else console.log(error);
        });
    };

    // re-open a converted quote: it goes back to Pending (pricing controls re-enabled) but stays linked to its order
    const Reopen = () => {
      serverErrorMessage.value = null;
      loading.value = true;
      store.dispatch('app/reopenQuote', { id: route.value.params.id })
        .then(() => {
          loading.value = false;
        })
        .catch(error => {
          loading.value = false;
          if (error && error.message) serverErrorMessage.value = error.message;
          else console.log(error);
        });
    };

    // abandon a re-quote: restores the quote's saved pricing (the form is reloaded to show it) and the order's
    // original invoice becomes payable again
    const CancelRequote = () => {
      serverErrorMessage.value = null;
      loading.value = true;
      store.dispatch('app/cancelRequote', { id: route.value.params.id })
        .then(() => {
          loading.value = false;
          fetchData(route.value.params.id);
        })
        .catch(error => {
          loading.value = false;
          if (error && error.message) serverErrorMessage.value = error.message;
          else console.log(error);
        });
    };

    const Back = () => router.push({ name: 'order-quoteslist' });

    return {
      Convert,
      Reopen,
      CancelRequote,
      quoteLoaded,
      loading,
      form,
      entity,
      readOnly,
      cm,
      statusColor,
      Display,
      validators,
      serverErrorMessage,
      form_PricingMethod,
      form_CompensationRequired,
      form_HeavyItem,
      form_DangerousGoods,
      form_PackagingRequired,
      form_HIABCharge,
      form_ExtraIds,
      showExtra,
      isAdminOnlyExtra,
      otherExtraOptions,
      selectedOtherExtras,
      form_Remarks,
      boxes,
      addBox,
      removeBox,
      form_MultipleOrders,
      productMode,
      boxQty,
      boxProductPrice,
      boxProductLabel,
      validBoxes,
      pricedBoxes,
      orderCount,
      remarksRequired,
      heavyDisabled,
      heavyNote,
      volumetricLines,
      convertedOrders,
      onBoxDimensionInput,
      onBoxProductInput,
      extraTotal,
      effectiveCost,
      surchargePrice,
      totalEstimated,
      canReject,
      canConvert,
      convertDisabledReason,
      Save,
      Reject,
      Back,
      icons: {
        mdiContentSaveOutline,
        mdiChevronLeft,
        mdiClose,
        mdiPlus,
        mdiRefresh,
        mdiOpenInNew
      }
    };
  }
}
</script>

<style lang="scss" scoped>
.readonly-row {
  display: flex;
  padding: 4px 0;
}
.readonly-row .label {
  min-width: 150px;
  font-weight: 600;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}
</style>
